/**
 * OC Chat Component
 * Renders the chat interface with all providers after authentication
 */

import React from 'react';
import { ThemeProvider } from 'styled-components';

import { AppContextProvider } from '../../../../store/App/store';
import { TranscriptContextProvider } from '../../../../store/transcript/store';
import { useTranscriptContext } from '../../../../store/transcript/utils/useTranscriptContext';
import { IAppTheme } from '../../../../styles/IAppTheme';
import { OcChatConfig, OcWidgetOptions } from '../../../../types/oc';
import ChatBackgroundSVG from '../../../BannerSVG/ChatBackgroundSVG';
import { ChatHeader } from '../../../ChatHeader/ChatHeader';
import { MessageType } from '../../../Message/types/messages';
import { MessageInputBox } from '../../../MessageInputBox/MessageInputBox';
import { Transcript } from '../../../TranscriptBox/Transcript';
import {
  getOcDefaultInitialMessage,
  OC_DEFAULT_PLACEHOLDER_MESSAGE,
  OC_DEFAULT_UI_CONFIG,
  OC_SYSTEM_CONFIG,
} from '../../config/ocDefaults';
import { useDelayInitAiChatEndpoints } from '../../hooks/useAiAssistantEndpoints';
import {
  createOcRenderAdditionalActions,
  createOcRenderAdditionalMessageUI,
} from '../../rendering/ocMessageRendering';
import { SubmitDiscussionResponse } from '../../types/apiTypes';
import AskHumanBox from '../AskHuman/AskHumanBox';
import AskHumanModal from '../AskHuman/AskHumanModal';
import { OcAskHumanProvider } from '../AskHuman/OcAskHumanProvider';
import { OcChatFooter } from '../Footer/OcChatFooter';
import WithVerticalSources from '../Sources/WithVerticalSources';
import {
  OcChatContent,
  OcChatLayout,
  OcChatWrapper,
  OcHeaderWrapper,
  OcMessageInputWrapperActive,
  OcMessageInputWrapperInitial,
  OcTranscriptWrapper,
} from './OcChat.styled';

export interface OcChatProps {
  config: OcChatConfig;
  userId: string;
  theme: IAppTheme;
  options?: OcWidgetOptions;
}

/**
 * OcChat Component
 * Sets up all providers and configuration for the authenticated chat experience
 */
export const OcChat: React.FC<OcChatProps> = ({ config, userId, theme, options }) => {
  // Create endpoints with delayed initialization (matches pattern from main OC widget)
  const endpoints = useDelayInitAiChatEndpoints(config);
  const endpointConfig = {
    systemId: OC_SYSTEM_CONFIG.systemId,
    tenantId: config.tenantId,
    userId: userId,
  };

  // Merge UI options with defaults in a single object
  const uiConfig = {
    ...OC_DEFAULT_UI_CONFIG,
    ...options,
  };
  
  const {
    title,
    welcomeMessage,
    showBetaBadge,
    footerText,
    askHumanButtonText,
    askHumanPromptText,
    showHorizontalSources,
    showVerticalSources,
    assistantName,
    avatarUrl,
  } = uiConfig;

  // Build messages from welcomeMessage
  const initialMessage: MessageType = getOcDefaultInitialMessage(welcomeMessage);
  const placeholderMessage: MessageType = OC_DEFAULT_PLACEHOLDER_MESSAGE;

  // Rendering functions - use OC defaults or allow override
  // Use factory functions to respect showHorizontalSources setting
  const renderAdditionalMessageUI = options?.renderAdditionalMessageUI || createOcRenderAdditionalMessageUI(showHorizontalSources);
  const renderAdditionalActions = options?.renderAdditionalActions || createOcRenderAdditionalActions(showHorizontalSources);

  // Success/error handlers for Ask Human
  const handlePostSuccess = (response: SubmitDiscussionResponse) => {
    if (options?.onPostDiscussionSuccess) {
      options.onPostDiscussionSuccess(response);
    }
  };

  const handlePostError = (err: Error) => {
    if (options?.onError) {
      options.onError(err);
    }
  };


  return (
    <AppContextProvider endpoints={endpoints} config={endpointConfig}>
      <ThemeProvider theme={theme}>
        <OcAskHumanProvider config={config} onSuccess={handlePostSuccess} onError={handlePostError}>
          <TranscriptContextProvider
            initialSessionId=""
            initialMessage={initialMessage}
            placeholderMessage={placeholderMessage}
            onChatChange={options?.onChatChange}
          >
            <OcChatInnerContent
              title={title}
              options={options}
              showBetaBadge={showBetaBadge}
              renderAdditionalActions={renderAdditionalActions}
              renderAdditionalMessageUI={renderAdditionalMessageUI}
              askHumanButtonText={askHumanButtonText}
              askHumanPromptText={askHumanPromptText}
              showVerticalSources={showVerticalSources}
              footerText={footerText}
              assistantName={assistantName}
              avatarUrl={avatarUrl}
            />
            
            {/* Ask Human Modal - gets state from context */}
            <AskHumanModal />
          </TranscriptContextProvider>
        </OcAskHumanProvider>
      </ThemeProvider>
    </AppContextProvider>
  );
};

/**
 * Inner content component that has access to TranscriptContext
 * Separated to allow reading transcript state for initial state detection
 */
const OcChatInnerContent: React.FC<{
  title?: string;
  options?: OcWidgetOptions;
  showBetaBadge?: boolean;
  renderAdditionalActions: (message: MessageType) => React.ReactNode;
  renderAdditionalMessageUI: (message: MessageType) => React.ReactNode;
  askHumanButtonText: string;
  askHumanPromptText: string;
  showVerticalSources: boolean;
  footerText?: string;
  assistantName?: string;
  avatarUrl?: string;
}> = ({ 
  title = '', 
  options, 
  showBetaBadge = false, 
  renderAdditionalActions, 
  renderAdditionalMessageUI, 
  askHumanButtonText,
  askHumanPromptText,
  showVerticalSources,
  footerText,
  assistantName,
  avatarUrl,
}) => {
  const { state } = useTranscriptContext();
  const hasMessages = state.messages && state.messages.length > 0;
  const isInitialState = !hasMessages || state.messages.length <= 1;

  // Only show transcript footer when not in initial state
  // If vertical sources are hidden, show AskHumanBox in transcript footer instead
  const transcriptFooterContent = isInitialState ? undefined : (
    !showVerticalSources ? (
      <AskHumanBox
        promptText={askHumanPromptText}
        buttonText={askHumanButtonText}
        inTranscriptFooter={true}
      />
    ) : options?.transcriptFooter
  );

  // Change background when vertical sources are hidden - use chat bg instead of transparent
  const transcriptBackgroundColor = showVerticalSources || isInitialState ? 'transparent' : undefined;

  return (
    <>
      {/* OC-specific wrapper with header separate from SVG area */}
      <OcChatWrapper>
        {/* Header without SVG background */}
        <OcHeaderWrapper>          
          <ChatHeader
            title={title}
            branding={options?.branding}
            showBetaBadge={showBetaBadge}
          />
        </OcHeaderWrapper>
        {/* Content area (transcript + input + footer) */}
        <OcChatContent>
          <ChatBackgroundSVG />
          <div style={{ position: 'relative', zIndex: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <WithVerticalSources
              shouldDisplayAskHumanBox={true}
              askHumanButtonText={askHumanButtonText}
              askHumanPromptText={askHumanPromptText}
              showVerticalSources={showVerticalSources}
            >
              <OcChatLayout $isInitialState={isInitialState}>
                <OcTranscriptWrapper $isInitialState={isInitialState}>
                  <Transcript
                    loadingMessage={options?.loadingMessage}
                    renderMessage={options?.renderMessage}
                    renderAdditionalActions={renderAdditionalActions}
                    renderAdditionalMessageUI={renderAdditionalMessageUI}
                    transcriptFooter={transcriptFooterContent}
                    backgroundColor={transcriptBackgroundColor}
                    border="none"
                    margin={!showVerticalSources ? '16px 16px 0 16px' : undefined}
                    borderRadius={!showVerticalSources ? '0.5rem' : undefined}
                    assistantName={assistantName}
                    avatarUrl={avatarUrl}
                  />
                </OcTranscriptWrapper>
                {isInitialState ? (
                  <OcMessageInputWrapperInitial>
                    <MessageInputBox minHeight="2rem" placeholder={options?.inputPlaceholder ?? undefined} noBorder={false} />
                  </OcMessageInputWrapperInitial>
                ) : (
                  <OcMessageInputWrapperActive>
                    <MessageInputBox minHeight="2rem" placeholder={options?.inputPlaceholder ?? undefined} noBorder={false} />
                    <OcChatFooter footerText={footerText} askHumanButtonText={askHumanButtonText} />
                  </OcMessageInputWrapperActive>
                )}
              </OcChatLayout>
            </WithVerticalSources>
            </div>
        </OcChatContent>
      </OcChatWrapper>
    </>
  );
};

export default OcChat;
